// Dónde va cada etiqueta de la curva de "Tu escalera hoy" para que ninguna se encime, tape un punto de
// desbloqueo ni se salga de la gráfica. Cada etiqueta va arriba de su punto; si no cabe, abajo; si
// choca con "AQUÍ VAS", apilada encima de ella; y si nada cabe, no se pinta (su desbloqueo queda en el
// texto accesible de la curva). Entre los lados que caben se prefiere el que no cruza la curva. Los
// anchos se estiman por número de caracteres.

/** La gráfica, en unidades del viewBox (como el mock: 620×240). */
export const CHART_WIDTH = 620;
export const CHART_HEIGHT = 240;
export const PAD_LEFT = 52;
export const PAD_RIGHT = 20;
export const PAD_TOP = 24;
export const PAD_BOTTOM = 34;
/** El piso de la curva. */
export const BASELINE = CHART_HEIGHT - PAD_BOTTOM;

export type Anchor = 'start' | 'middle' | 'end';

export interface LabelLine {
    /** Caracteres, para estimar el ancho. */
    chars: number;
    size: number;
    caps: boolean;
}

export interface LabelSpec<L extends LabelLine = LabelLine> {
    key: string;
    /** El punto al que acompaña. */
    x: number;
    y: number;
    /** Distancia del punto al renglón más cercano. */
    gap: number;
    lines: [L, ...L[]];
}

export interface Box {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

export interface PlacedLabel<S extends LabelSpec = LabelSpec> {
    spec: S;
    anchor: Anchor;
    /** Renglón base de cada línea. */
    ys: number[];
    box: Box;
}

export interface CurvePoint {
    x: number;
    y: number;
}

export interface LayoutContext {
    /** Los puntos de desbloqueo y el de "AQUÍ VAS": ninguna etiqueta los tapa, salvo la suya. */
    dots: ReadonlyArray<{ key: string; box: Box }>;
    curve: readonly CurvePoint[];
}

const LINE_STEP = 11;
/** Ancho medio de un carácter en em; las mayúsculas espaciadas ocupan más. */
const CHAR_EM = 0.62;
const CAPS_CHAR_EM = 0.74;

const lineWidth = (line: LabelLine) =>
    line.chars * line.size * (line.caps ? CAPS_CHAR_EM : CHAR_EM);

export const dotBox = (x: number, y: number, radius: number): Box => ({
    left: x - radius,
    right: x + radius,
    top: y - radius,
    bottom: y + radius,
});

function spanOf(anchor: Anchor, x: number, width: number): { left: number; right: number } {
    switch (anchor) {
        case 'start':
            return { left: x, right: x + width };
        case 'end':
            return { left: x - width, right: x };
        case 'middle':
            return { left: x - width / 2, right: x + width / 2 };
    }
}

/**
 * Al centro del punto; pegada a la izquierda o a la derecha si al centro invadiría el eje o se saldría.
 * Si aun así no cabe, la etiqueta no se pinta: cualquier otra ancla se sale más.
 */
function anchorAt(x: number, width: number): { anchor: Anchor; left: number; right: number } {
    let anchor: Anchor = 'middle';
    if (x - width / 2 < PAD_LEFT) anchor = 'start';
    else if (x + width / 2 > CHART_WIDTH - 2) anchor = 'end';
    return { anchor, ...spanOf(anchor, x, width) };
}

function placeAt<S extends LabelSpec>(spec: S, side: 'above' | 'below'): PlacedLabel<S> {
    const { lines } = spec;
    const firstBelow = spec.y + spec.gap + 3 + lines[0].size * 0.8;
    const ys = lines.map((_, i) =>
        side === 'above'
            ? spec.y - spec.gap - (lines.length - 1 - i) * LINE_STEP
            : firstBelow + i * LINE_STEP,
    );
    const { anchor, left, right } = anchorAt(spec.x, Math.max(...lines.map(lineWidth)));
    const last = lines.at(-1) ?? lines[0];
    return {
        spec,
        anchor,
        ys,
        box: {
            left,
            right,
            top: ys[0] - lines[0].size * 0.8,
            bottom: (ys.at(-1) ?? ys[0]) + last.size * 0.25,
        },
    };
}

function shifted<S extends LabelSpec>(label: PlacedLabel<S>, dy: number): PlacedLabel<S> {
    return {
        ...label,
        ys: label.ys.map((y) => y + dy),
        box: { ...label.box, top: label.box.top + dy, bottom: label.box.bottom + dy },
    };
}

const overlaps = (a: Box, b: Box) =>
    a.left < b.right + 2 && b.left < a.right + 2 && a.top < b.bottom + 2 && b.top < a.bottom + 2;

/** La altura de la curva en `px`; `null` fuera de ella. */
function curveAt(curve: readonly CurvePoint[], px: number): number | null {
    for (let i = 1; i < curve.length; i++) {
        const a = curve[i - 1];
        const b = curve[i];
        if (px < a.x || px > b.x) continue;
        return b.x === a.x ? a.y : a.y + ((px - a.x) / (b.x - a.x)) * (b.y - a.y);
    }
    return null;
}

function crossesCurve(box: Box, curve: readonly CurvePoint[]): boolean {
    for (let px = box.left; px <= box.right; px += 2) {
        const cy = curveAt(curve, px);
        if (cy !== null && cy > box.top && cy < box.bottom) return true;
    }
    return false;
}

/** Dentro del área de la curva: sin pisar las marcas del eje Y ni salirse, y entre el padding de arriba y el piso. */
const inBounds = (label: PlacedLabel) =>
    label.box.left >= PAD_LEFT &&
    label.box.right <= CHART_WIDTH &&
    label.ys[0] >= PAD_TOP &&
    label.box.bottom <= BASELINE - 2;

const fits = (label: PlacedLabel, placed: readonly PlacedLabel[], ctx: LayoutContext) =>
    inBounds(label) &&
    placed.every((other) => !overlaps(label.box, other.box)) &&
    ctx.dots.every((dot) => dot.key === label.spec.key || !overlaps(label.box, dot.box));

/** La primera etiqueta ("AQUÍ VAS") siempre se pinta: arriba si cabe, abajo si no, y si no, arriba acotada. */
function placeFirst<S extends LabelSpec>(spec: S): PlacedLabel<S> {
    const above = placeAt(spec, 'above');
    if (inBounds(above)) return above;
    const below = placeAt(spec, 'below');
    if (inBounds(below)) return below;
    return shifted(above, PAD_TOP - above.ys[0]);
}

export function layoutLabels<S extends LabelSpec>(
    first: S,
    rest: readonly S[],
    ctx: LayoutContext,
): PlacedLabel<S>[] {
    const here = placeFirst(first);
    const placed: PlacedLabel<S>[] = [here];
    for (const spec of rest) {
        const above = placeAt(spec, 'above');
        const options = [above, placeAt(spec, 'below')].filter((label) => fits(label, placed, ctx));
        let choice = options.find((label) => !crossesCurve(label.box, ctx.curve)) ?? options.at(0);
        // Si choca con "AQUÍ VAS", se apila encima de ella.
        if (!choice && overlaps(above.box, here.box)) {
            const stacked = shifted(above, here.box.top - 3 - above.box.bottom);
            if (fits(stacked, placed, ctx)) choice = stacked;
        }
        if (choice) placed.push(choice);
    }
    return placed;
}
